import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { track } from "../lib/analytics";

const iOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

export const InstallPWA = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);

  useEffect(() => {
    // Already installed as a standalone / from home-screen
    const standalone = window.matchMedia("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
    if (standalone) return;

    // Suppressed by user in last 7 days
    const dismissedAt = Number(localStorage.getItem("install_dismissed_at") || 0);
    if (dismissedAt && Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
      track("pwa_install_prompted");
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    const onInstalled = () => track("pwa_installed");
    window.addEventListener("appinstalled", onInstalled);

    // iOS has no beforeinstallprompt — show a manual hint after 20 seconds on first visit
    if (iOS()) {
      const t = setTimeout(() => { setShowIOSHint(true); track("pwa_ios_hint_shown"); }, 20_000);
      return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); clearTimeout(t); };
    }
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    track("pwa_install_choice", { outcome: choice?.outcome || "unknown" });
    setDeferred(null);
    setVisible(false);
  };

  const dismiss = () => {
    track("pwa_install_dismissed");
    localStorage.setItem("install_dismissed_at", String(Date.now()));
    setVisible(false);
    setShowIOSHint(false);
  };

  if (!visible && !showIOSHint) return null;

  return (
    <div
      data-testid="install-pwa"
      className="fixed bottom-6 right-6 z-40 max-w-xs bg-[#0a0a0a] border border-[color:var(--accent-matrix)]/50 p-4 reveal"
      style={{ boxShadow: "0 0 30px rgba(0,255,170,0.1)" }}
    >
      <button
        data-testid="install-pwa-dismiss"
        onClick={dismiss}
        className="absolute top-2 right-2 text-neutral-500 hover:text-white p-1"
        style={{ transitionProperty: "color", transitionDuration: "150ms" }}
      >
        <X className="w-3 h-3" />
      </button>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent-matrix)] mb-2">
        <Download className="w-3 h-3" /> install as app
      </div>
      {visible && (
        <>
          <div className="text-sm text-neutral-200 mb-3">
            Add sAimonSays.Ai to your home screen for a full-screen, app-like experience.
          </div>
          <button
            data-testid="install-pwa-install"
            onClick={install}
            className="bg-[color:var(--accent-matrix)] text-black px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold hover:bg-white duration-150"
            style={{ transitionProperty: "background-color" }}
          >
            install
          </button>
        </>
      )}
      {showIOSHint && !visible && (
        <div className="text-xs text-neutral-300 leading-relaxed">
          On iPhone: tap the <span className="text-[color:var(--accent-amber)]">Share</span> icon, then{" "}
          <span className="text-[color:var(--accent-amber)]">Add to Home Screen</span>.
        </div>
      )}
    </div>
  );
};
